import { SearchResult, SearchFilters } from '@/types/search'
import { userService } from './user.service'
import { workspaceService } from './workspace.service'
import { projectService } from './project.service'
import { taskService } from './task.service'
import { messageService } from './message.service'
import { documentService } from './document.service'

export class SearchService {
  /**
   * Search across all nodes by title/name
   */
  async search(query: string, filters?: SearchFilters): Promise<SearchResult[]> {
    if (!query.trim()) {
      return []
    }

    const searchPromises = []

    // Parse types filter - it can be either an array or a JSON string
    let typesFilter: string[] | undefined
    if (filters?.types) {
      try {
        typesFilter = typeof filters.types === 'string' ? JSON.parse(filters.types) : filters.types
      } catch {
        typesFilter = undefined
      }
    }

    // Search users by name and email
    if (!typesFilter || typesFilter.includes('user')) {
      searchPromises.push(
        userService.searchUsers(query).then((users) =>
          users.map(
            (user): SearchResult => ({
              id: user.id,
              type: 'user',
              title: user.name,
              subtitle: user.email,
              url: `/users/${user.id}`,
              node: user,
            })
          )
        )
      )
    }

    // Search workspaces by name
    if (!typesFilter || typesFilter.includes('workspace')) {
      searchPromises.push(
        workspaceService.searchWorkspaces(query).then((workspaces) =>
          workspaces.map(
            (workspace): SearchResult => ({
              id: workspace.id,
              type: 'workspace',
              title: workspace.name,
              subtitle: workspace.email,
              url: `/workspaces/${workspace.id}`,
              node: workspace,
            })
          )
        )
      )
    }

    // Search projects by name and description
    if (!typesFilter || typesFilter.includes('project')) {
      searchPromises.push(
        projectService.searchProjects(query).then((projects) =>
          projects.map(
            (project): SearchResult => ({
              id: project.id,
              type: 'project',
              title: project.name,
              subtitle: project.description,
              url: `/projects/${project.id}`,
              node: project,
            })
          )
        )
      )
    }

    // Search tasks by name and description
    if (!typesFilter || typesFilter.includes('task')) {
      searchPromises.push(
        taskService.searchTasks(query).then((tasks) =>
          tasks.map(
            (task): SearchResult => ({
              id: task.id,
              type: 'task',
              title: task.name,
              subtitle: task.description,
              url: `/tasks/${task.id}`,
              node: task,
            })
          )
        )
      )
    }

    // Search messages by title and content
    if (!typesFilter || typesFilter.includes('message')) {
      searchPromises.push(
        messageService.searchMessages(query).then((messages) =>
          messages.map(
            (message): SearchResult => ({
              id: message.id,
              type: 'message',
              title: message.name,
              subtitle:
                message.description.substring(0, 100) +
                (message.description.length > 100 ? '...' : ''),
              url: `/messages/${message.id}`,
              node: message,
            })
          )
        )
      )
    }

    // Search documents by name and description
    if (!typesFilter || typesFilter.includes('document')) {
      searchPromises.push(
        documentService.searchDocuments(query).then((documents) =>
          documents.map(
            (document): SearchResult => ({
              id: document.id,
              type: 'document',
              title: document.name,
              subtitle: document.description,
              url: `/documents/${document.id}`,
              node: document,
            })
          )
        )
      )
    }

    try {
      const resultsArrays = await Promise.all(searchPromises)
      const allResults = resultsArrays.flat()

      // Sort by relevance (prioritize exact matches in title, then partial matches)
      allResults.sort((a, b) => {
        const aExact = a.title.toLowerCase() === query.toLowerCase()
        const bExact = b.title.toLowerCase() === query.toLowerCase()

        if (aExact && !bExact) return -1
        if (!aExact && bExact) return 1

        // For non-exact matches, sort by title length (shorter = more relevant)
        return a.title.length - b.title.length
      })

      // Apply limit if specified
      return filters?.limit ? allResults.slice(0, filters.limit) : allResults
    } catch (error) {
      console.error('Search error:', error)
      throw new Error('Failed to perform search')
    }
  }

  /**
   * Get search suggestions for autocomplete (limited results)
   */
  async getSuggestions(query: string): Promise<SearchResult[]> {
    return this.search(query, { limit: 8 })
  }
}

export const searchService = new SearchService()
